(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define(['backbone',
            'jquery',
            './logger',
            'app/storage/localStorageService'], factory);
  } else if (typeof exports === 'object') {
    // CommonJS-style exports
    var Backbone = require('backbone');
    var $ = require('jquery');
    var Logger = require('./logger');
    var localStorageService = require('./storage/localStorageService');

    module.exports = factory(Backbone, $, Logger, localStorageService);
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function(Backbone, $, Logger, localStorageService) {
  'use strict';

  var LogModel = Backbone.Model.extend({
    // Default values for the log object. This will suffice for most logs.
    defaults: {
      userAgent: window.navigator.userAgent || 'no user agent',
      platform: window.navigator.platform || 'no platform',
      vendor: window.navigator.vendor || 'no vendor',
      location: document.URL || window.location.href || 'no location detected',
      timestamp: new Date().getTime(),
      messages: []
    }
  });

  var remoteMessageBacklogProperty = localStorageService.LOCAL_STORAGE_KEYS.remoteLoggerMessageBacklog;

  return {
    /**
     * URL of the logger back end. Logs are posted to this URL.
     * @property remoteServerUrl
     * @type {String}
     * @private
     */
    remoteServerUrl: '/',

    /**
     * Prepares the remoteLogger for use, and attempts to send backlogged messages from localStorage.
     *
     * @method init
     * @param {String} remoteUrl URL of the logger back end.
     */
    init: function(remoteUrl) {
      this.remoteServerUrl = remoteUrl || this.remoteServerUrl;
      this.sendBackloggedMessages();
    },

    /**
     * Sends a message to the server with backlogging enabled.
     *
     * @method log
     * @param {Object} ...message  Message objects to log to the server. An unlimited number of messages, separated by commas,
     * are allowed: this method follows the same convention as console.log.
     */
    log: function() {
      var argsArray = Array.prototype.slice.call(arguments);
      this.logSend(argsArray, true);
    },

    /**
     * Sends a message to the server with backlogging disabled (so if sending fails once, it will not happen at all).
     *
     * @method logOnce
     * @param {Object} ...message  Message objects to log to the server. An unlimited number of messages, separated by commas,
     * are allowed: this method follows the same convention as console.log.
     */
    logOnce: function() {
      var argsArray = Array.prototype.slice.call(arguments);
      this.logSend(argsArray, false);
    },

    /**
     * Logs a message to the remote server.
     *
     * @method logSend
     * @private
     * @param {Object[]} messageList Array of messages to be sent to the server. This array should come from the arguments object of another log method.
     * @param {Boolean} enableBacklog Boolean indicating whether or not messages that fail to send should be placed in the backlog.
     */
    logSend: function(messageList, enableBacklog) {
      var logObject = new LogModel(),
      additionalErrorInfo,
      remoteLogger = this;

      logObject.set('messages', messageList);
      logObject.url = this.remoteServerUrl;

      logObject.save(null, {
        dataType: 'text', // Necessary since the server doesn't return JSON; it just returns 200

        error: function () {
          Logger.error('remoteLogger: Error while sending object to server. Attempting to send error information.');
          additionalErrorInfo = {
            eventType: 'internalError',
            time: new Date().getTime(),
            errorStack: new Error().stack // We can't get an exception stack trace, but we can generate one
          };

          // Try to send again, this time with error information. Success here rules out a connection problem.
          logObject.set('messages', additionalErrorInfo);
          logObject.save(null, {
            dataType: 'text',

            // Two failures, so we'll cache the changes and try again later.
            error: function() {
              Logger.error('remoteLogger: Error while sending error object to server. Are you connected to the Internet? The original message has been backlogged.');
              if (enableBacklog) {
                remoteLogger.addBackloggedMessage(messageList);
              }
            },

            success: function () {
              Logger.info('remoteLogger: Successfully sent error information to remote server.');
            }
          });
        }
      });
    },

    /**
     * Sends messages backlogged in localStorage to the remote server.
     *
     * @method sendBackloggedMessages
     * @private
     */
    sendBackloggedMessages: function() {
      var backlog,
          remoteLogger = this;

      // get and setBackloggedMessages degrade gracefully so we don't need to check if localStorage exists
      backlog = this.getBackloggedMessages();

      // Any messages that fail to send will go back into this backlog, so we should clear it before we start trying to send
      this.setBackloggedMessages([]);

      $.each(backlog, function(index, message) {
        remoteLogger.log(message); // The message will be sent back to the backlog if it fails to send again
      });
    },

    /**
     * Adds a message to the backlog. Has no effect if localStorage does not exist.
     *
     * @method addBackloggedMessage
     * @private
     * @param {Object} message The message to add to the backlog.
     */
    addBackloggedMessage: function(message) {
      var backlog = this.getBackloggedMessages();
      backlog.push(message);
      this.setBackloggedMessages(backlog);
    },

    /**
     * Getter for the message backlog, which is stored in HTML5 localStorage. If localStorage doesn't exist, this method returns an
     * empty array.
     *
     * @method getBackloggedMessages
     * @private
     * @return {Array} the array of backlogged message items.
     */
    getBackloggedMessages: function() {
      return localStorageService.getItem(remoteMessageBacklogProperty) || [];
    },

    /**
     * Setter for the message backlog. If localStorage doesn't exist, this method has no effect.
     *
     * @method setBackloggedMessages
     * @private
     */
    setBackloggedMessages: function (backlog) {
      localStorageService.setItem(remoteMessageBacklogProperty, backlog);
    }
  };
}));
